import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import dateFormat from "dateformat";
import Api from "../../../services/Api";

const plugins = {
  afterDraw: function (chart) {
    if (chart.data.datasets.length > 0) {
      if (chart.data.datasets[0].data.length === 0) {
        var width = chart.width,
          height = chart.height,
          ctx = chart.ctx;
        ctx.restore();
        ctx.font = "14px sans-serif";
        ctx.textBaseline = "middle";
        ctx.textAlign = "center";
        ctx.fillStyle = "#6c757d";
        var text = "Nenhuma Informação Disponível",
          textX = width / 2,
          textY = height / 2;
        ctx.fillText(text, textX, textY);
        ctx.save();
      }
    }
  },
}

let delayed;

const options = {
  responsive: true,
  maintainAspectRatio: true,
  scales: {
    y: {
      beginAtZero: false,
      grid: {
        color: 'rgba(28, 66, 32, 0.1)',
        drawBorder: false,
      },
      ticks: {
        color: '#1c4220',
        font: {
          size: 9,
          weight: 500
        },
        padding: 4
      }
    },
    x: {
      grid: {
        display: false,
        drawBorder: false,
      },
      ticks: {
        color: '#6c757d',
        font: {
          size: 8,
          weight: 500
        },
        maxRotation: 45,
        minRotation: 45,
        padding: 2
      }
    },
  },
  animations: {
    radius: {
      duration: 400,
      easing: 'easeInOutQuart',
      loop: (context) => context.active
    }
  },
  animation: {
    onComplete: () => {
      delayed = true;
    },
    delay: (context) => {
      let delay = 0;
      if (context.type === 'data' && context.mode === 'default' && !delayed) {
        delay = context.dataIndex * 3 + context.datasetIndex * 1;
      }
      return delay;
    },
  },
  hoverRadius: 5,
  hoverBackgroundColor: '#8fad15',
  hoverBorderColor: '#1c4220',
  hoverBorderWidth: 2,
  interaction: {
    mode: 'index',
    intersect: false,
    axis: 'x'
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      align: 'end',
      labels: {
        color: '#1c4220',
        font: {
          size: 9,
          weight: 600
        },
        padding: 6,
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 5,
        boxHeight: 5
      }
    },
    tooltip: {
      enabled: true,
      backgroundColor: 'rgba(28, 66, 32, 0.95)',
      titleColor: '#8fad15',
      bodyColor: '#ffffff',
      borderColor: '#8fad15',
      borderWidth: 1,
      padding: 12,
      displayColors: true,
      titleFont: {
        size: 12,
        weight: 'bold'
      },
      bodyFont: {
        size: 11
      },
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            label += context.parsed.y + '%';
          }
          return label;
        }
      }
    },
    title: {
      display: false,
    }
  }
};

let width, height, gradient, bgGradient;
function getGradient(ctx, chartArea) {
  const chartWidth = chartArea.right - chartArea.left;
  const chartHeight = chartArea.bottom - chartArea.top;
  if (!gradient || width !== chartWidth || height !== chartHeight) {
    width = chartWidth;
    height = chartHeight;
    gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

    gradient.addColorStop(1, "#8fad15");
    gradient.addColorStop(0.5, "#1c4220");
    gradient.addColorStop(0, "#dc3545");
  }
  return gradient;
}

function getBackgroundGradient(ctx, chartArea) {
  const chartWidth = chartArea.right - chartArea.left;
  const chartHeight = chartArea.bottom - chartArea.top;
  if (!bgGradient || width !== chartWidth || height !== chartHeight) {
    width = chartWidth;
    height = chartHeight;
    bgGradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

    bgGradient.addColorStop(1, "rgba(143, 173, 21, 0.1)");
    bgGradient.addColorStop(0.5, "rgba(28, 66, 32, 0.05)");
    bgGradient.addColorStop(0, "rgba(220, 53, 69, 0.1)");
  }
  return bgGradient;
}

const Grafico = ({ serial, company, reload, detailsEquip, totalEquipments }) => {
  const { configs } = company;

  const equipamento = useMemo(() => {
    return detailsEquip.find((equip) => equip?.serialnumber === serial);
  }, [serial, detailsEquip]);

  const getColSize = () => {
    if (totalEquipments === 1) return 12;
    if (totalEquipments <= 4) return 6;
    return 4;
  };

  const getHeightSize = () => {
    if (totalEquipments === 1) return 60;
    if (totalEquipments === 2) return 110;
    if (totalEquipments <= 4) return 50;
    return 90;
  };

  const [medidas, setMedidas] = useState([]);
  const [status, setStatus] = useState(null);
  const [ultimaMedida, setUltimaMedida] = useState({
    _id: "",
    serialnumber: "",
    date: "",
    idcalib: "",
    grain: "",
    humidity: "",
    temperature: "",
    peso: "",
    freqc: "",
    freqv: "",
    ph: "",
    curvavalidade: "",
    companyId: "",
    localEquipament: ""
  });

  const handleEquipment = useCallback(async () => {
    try {
      let { data } = await Api.get(`/api/v1/telemetria?serial=${serial}&id=${company._id}`);

      if (Array.isArray(data) && data.length > 0) {
        const reversedData = [...data].reverse(); // Cria uma cópia e inverte para não mutar o original
        setMedidas(reversedData);
        setUltimaMedida(reversedData[reversedData.length - 1]); // O primeiro item agora é o mais recente
      } else {
        // Limpa dados antigos se não houver novos dados
        setMedidas([]);
      }
    } catch (error) {
      console.error('Erro ao buscar medidas:', error?.message);
      // Não limpa os dados em caso de erro para manter a última informação válida
    }
  }, [serial, company._id]);

  const handleGetStatus = useCallback(async () => {
    try {
      let { data } = await Api.get(`/connection/status/serial/${serial}`);
      setStatus(data?.status);
    } catch (error) {
      console.error('Erro ao buscar status:', error?.message);
      // Mantém o último status em caso de erro
    }
  }, [serial]);

  useEffect(() => {
    handleEquipment();
    handleGetStatus();
  }, [reload, handleEquipment, handleGetStatus]);

  const isWithin15Minutes = (lastDateString) => {
    if (!lastDateString) return false;
    const dateWithoutZ = lastDateString.replace('Z', '');
    const lastDate = new Date(dateWithoutZ).getTime();
    const now = new Date().getTime();
    const diffMs = now - lastDate;
    const diffMinutes = diffMs / (1000 * 60);
    return diffMinutes <= 20;
  };

  const isRecent = isWithin15Minutes(ultimaMedida.date);

  return (
    <Col xs={12} sm={12} md={12} lg={12} xl={getColSize()} xxl={getColSize()} className="mb-2">
      <Card 
        className="m-0 border-0" 
        style={{ 
          boxShadow: "0 4px 20px rgba(28, 66, 32, 0.1)", 
          borderRadius: "12px",
          background: "#ffffff",
          transition: "transform 0.2s ease, box-shadow 0.2s ease"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0 8px 25px rgba(28, 66, 32, 0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 4px 20px rgba(28, 66, 32, 0.1)";
        }}
      >
        <Card.Body className="p-3">
          <Row className="g-2 mb-3">
            <Col md={4} className="text-center">
              <div className="d-flex flex-column align-items-center">
                {status === "online" ?
                  <div style={{ 
                    fontSize: 10, 
                    background: "#8fad15", 
                    color: "#ffffff", 
                    fontWeight: 600, 
                    borderRadius: 12, 
                    padding: "2px 8px", 
                    marginBottom: 4
                  }}>
                    ONLINE
                  </div>
                  :
                  <div style={{ 
                    fontSize: 10, 
                    background: "#dc3545", 
                    color: "#ffffff", 
                    fontWeight: 600, 
                    borderRadius: 12, 
                    padding: "2px 8px", 
                    marginBottom: 4
                  }}>
                    OFFLINE
                  </div>
                }

                <div style={{ 
                  fontSize: 11, 
                  color: "#8fad15", 
                  fontWeight: 600,
                  marginBottom: 2
                }}>
                  {ultimaMedida?.serialnumber || "--"}
                </div>

                <h6 className="mb-0" style={{ 
                  color: "#1c4220", 
                  fontWeight: 700,
                  fontSize: "0.75rem"
                }}>
                  {equipamento?.nameEquipament?.toUpperCase() || "--"}
                </h6>
              </div>
            </Col>

            <Col md={4} className="text-center">
              <div className="d-flex flex-column align-items-center">
                <div style={{ 
                  fontSize: 10, 
                  color: "#8fad15", 
                  fontWeight: 600,
                  textTransform: "uppercase",
                  marginBottom: 4
                }}>
                  Grão
                </div>
                <h6 className="mb-0" style={{ 
                  color: "#1c4220", 
                  fontWeight: 700,
                  fontSize: "0.75rem"
                }}>
                  {ultimaMedida?.grain?.toUpperCase() || "--"}
                </h6>
              </div>
            </Col>

            <Col md={4} className="text-center">
              <div className="d-flex flex-column align-items-center">
                <div style={{ 
                  fontSize: 10, 
                  color: "#8fad15", 
                  fontWeight: 600,
                  textTransform: "uppercase",
                  marginBottom: 4
                }}>
                  Última Medida
                </div>
                <h6 className="mb-0" style={{ 
                  color: isRecent ? "#8fad15" : "#dc3545", 
                  fontWeight: 700,
                  fontSize: "0.7rem"
                }}>
                  {ultimaMedida?.date ? dateFormat(ultimaMedida.date, "UTC:dd/mm/yy' 'HH:MM") : "--"}
                </h6>
              </div>
            </Col>
          </Row>

          <Row className="mb-2 g-2">

            <Col md={4} className="text-center">
              <Card className="border-0 h-100" style={{
                background: "#f8f9fa",
                borderRadius: "6px",
                border: "1px solid #e9ecef"
              }}>
                <Card.Body className="p-2 d-flex flex-column justify-content-center">
                  <div style={{ 
                    fontSize: 10, 
                    color: "#8fad15", 
                    fontWeight: 600,
                    textTransform: "uppercase",
                    marginBottom: 4
                  }}>
                    Umidade
                  </div>
                  <div style={{ color: "#1c4220" }}>
                    <span style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                      {ultimaMedida?.humidity ? Math.floor(ultimaMedida.humidity) : "--"}
                    </span>
                    <span style={{ fontSize: "1.0rem", fontWeight: 600 }}>
                      {ultimaMedida?.humidity ? "." + (ultimaMedida.humidity % 1).toFixed(1).substring(2) : ""}
                    </span>
                    <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#8fad15" }}>
                      {ultimaMedida.humidity ? " %" : ""}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4} className="text-center">
              <Card className="border-0 h-100" style={{
                background: "#f8f9fa",
                borderRadius: "6px",
                border: "1px solid #e9ecef"
              }}>
                <Card.Body className="p-2 d-flex flex-column justify-content-center">
                  <div style={{ 
                    fontSize: 10, 
                    color: "#8fad15", 
                    fontWeight: 600,
                    textTransform: "uppercase",
                    marginBottom: 4
                  }}>
                    Temperatura
                  </div>
                  <div style={{ color: "#1c4220" }}>
                    <span style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                      {ultimaMedida?.temperature ? Math.floor(ultimaMedida.temperature) : "--"}
                    </span>
                    <span style={{ fontSize: "1.0rem", fontWeight: 600 }}>
                      {ultimaMedida?.temperature && "." + (ultimaMedida.temperature % 1).toFixed(1).substring(2)}
                    </span>
                    <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#8fad15" }}>
                      {ultimaMedida.temperature ? " °C" : ""}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4} className="text-center">
              <Card className="border-0 h-100" style={{
                background: "#f8f9fa",
                borderRadius: "6px",
                border: "1px solid #e9ecef"
              }}>
                <Card.Body className="p-2 d-flex flex-column justify-content-center">
                  <div style={{ 
                    fontSize: 10, 
                    color: "#8fad15", 
                    fontWeight: 600,
                    textTransform: "uppercase",
                    marginBottom: 4
                  }}>
                    {ultimaMedida.ph && Number(ultimaMedida.ph) < 200 ? "PH" : "Densidade"}
                  </div>
                  <div style={{ color: "#1c4220" }}>
                    {ultimaMedida?.ph ? (
                      <>
                        <span style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                          {Math.floor(ultimaMedida.ph)}
                        </span>
                        <span style={{ fontSize: "1.0rem", fontWeight: 600 }}>
                          .{(ultimaMedida.ph % 1).toFixed(1).split(".")[1]}
                        </span>
                        <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#8fad15" }}>
                          {Number(ultimaMedida.ph) < 200 ? " kg/hL" : " Kg/m³"}
                        </span>
                      </>
                    ) : (
                      <span style={{ fontSize: "1.5rem", fontWeight: 700 }}>--</span>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

          </Row>

          <div style={{
            background: "#fafbfc",
            borderRadius: "6px",
            padding: "12px",
            border: "1px solid #e9ecef"
          }}>
            <div style={{
              fontSize: 11,
              color: "#1c4220",
              fontWeight: 700,
              marginBottom: 8,
              paddingBottom: 6,
              borderBottom: "2px solid #8fad15"
            }}>
              HISTÓRICO DE UMIDADE
            </div>
            <Line
              data={{
                labels: medidas.map(data => data.date ? dateFormat(data.date, "UTC:dd/mm/yy' 'HH:MM") : "--"),
                datasets: [
                  {
                    label: `Máx`,
                    data: medidas.map(data => configs?.maxHumidity ? configs?.maxHumidity : null),
                    borderColor: "#dc3545",
                    backgroundColor: "rgba(220, 53, 69, 0.05)",
                    borderWidth: 2,
                    borderDash: [5, 5],
                    fill: false,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    tension: 0,
                    datalabels: {
                      display: false
                    },
                  },
                  {
                    label: "Umidade",
                    data: medidas.map((data) => (parseFloat(data?.humidityReal || data?.humidity).toFixed(1))),
                    borderColor: function (context) {
                      const chart = context.chart;
                      const { ctx, chartArea } = chart;
                      if (!chartArea) {
                        return "#8fad15";
                      }
                      return getGradient(ctx, chartArea);
                    },
                    backgroundColor: function (context) {
                      const chart = context.chart;
                      const { ctx, chartArea } = chart;
                      if (!chartArea) {
                        return "rgba(143, 173, 21, 0.1)";
                      }
                      return getBackgroundGradient(ctx, chartArea);
                    },
                    borderWidth: 3,
                    fill: true,
                    pointRadius: 3,
                    pointBackgroundColor: '#ffffff',
                    pointBorderColor: '#8fad15',
                    pointBorderWidth: 2,
                    pointHoverRadius: 5,
                    pointHoverBackgroundColor: '#8fad15',
                    pointHoverBorderColor: '#ffffff',
                    pointHoverBorderWidth: 2,
                    tension: 0.4,
                    datalabels: {
                      labels: {
                        title: {
                          color: '#1c4220',
                        }
                      },
                      display: 'auto',
                      align: 'end',
                      font: {
                        weight: 600,
                        size: 9,
                      }
                    },
                  },
                  {
                    label: `Mín`,
                    data: medidas.map(data => configs?.minHumidity ? configs?.minHumidity : null),
                    borderColor: "#8fad15",
                    backgroundColor: "rgba(143, 173, 21, 0.05)",
                    borderWidth: 2,
                    borderDash: [5, 5],
                    fill: false,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                    tension: 0,
                    datalabels: {
                      display: false
                    },
                  }
                ],
              }}
              plugins={[ChartDataLabels, plugins]}
              options={options}
              height={getHeightSize()}
            />
          </div>
        </Card.Body>
      </Card>
    </Col >
  );
};

export default Grafico;
